import { MapEmbed } from "@/components/MapEmbed";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { ContextLinkBlock } from "@/components/ContextLinkBlock";
import { SALON_SERVICE_LINKS } from "@/data/localServiceLinks";
import { BarberGrid } from "@/components/BarberGrid";
import { SALON_BARBERARE, salonBarberLine } from "@/data/barbers";
import { Button } from "@/components/ui/button";
import { salonBySlug } from "@/data/salons";
import { ReviewsBlock } from "@/components/ReviewsBlock";
import { breadcrumbSchema, faqSchema, jsonLd, localBusinessSchema, SITE_URL, absUrl } from "@/lib/seo";
import { reviewsForSalon } from "@/data/reviews";
import akersbergaEntre1200 from "@/assets/opt/akersberga-entre-1200.webp";
import akersbergaSalong800 from "@/assets/opt/akersberga-salong-800.webp";
import akersbergaSalong2800 from "@/assets/opt/akersberga-salong-2-800.webp";
import { withBookingUtm } from "@/lib/booking";
import { PRICE, priceRows } from "@/data/pricing";
import { formatCount, formatRating, salonReviewStats } from "@/data/reviewStats";
import { SalonRatingLines } from "@/components/SalonRatingLines";
const salon = salonBySlug["akersberga"];
const BOOK_URL = withBookingUtm("https://www.bokadirekt.se/places/tailors-barber-akersberga-ica-maxi-huset--37682");
const MAPS_LINK = "https://maps.app.goo.gl/41ZuapFZEmgFFbny7";
const MAPS_EMBED = `https://www.google.com/maps?q=${encodeURIComponent("Långhundravägen 9 Åkersberga")}&output=embed`;

// Omdömestal: enda källan är src/data/reviewStats.ts (avstämd 2026-08-29).
const stats = salonReviewStats["akersberga"];
const RATING = formatRating(stats.google.rating);
const COUNT = formatCount(stats.google.count);

const services = priceRows([
  "Herrklippning",
  "Skin Fade / Taper Fade",
  "Skäggtrimning",
  "Klippning & Skäggtrimning",
  "Barnklippning",
  "Seniorklippning 65+",
]);

const faq = [
  { q: "Var ligger Tailor's Barber i Åkersberga?", a: "Vi finns i ICA Maxi Huset i Åkersberga, Långhundravägen 9. Salongen ligger inne i galleriadelen vid ICA Maxi." },
  { q: "Är Tailor's Barber Åkersberga öppen på helgen?", a: "Ja, vi har öppet lördag och söndag 10:00–18:00. Vardagar håller vi öppet 10:00–19:00." },
  { q: "Hur mycket kostar herrklippning i Åkersberga?", a: "Herrklippning kostar 450 kr. Skäggtrimning 380 kr. Kombination hår + skägg 680 kr. Barnklippning 380 kr." },
  { q: "Kan jag boka skin fade i Åkersberga?", a: "Ja, vi utför taper fade och skin fade i Åkersberga. Boka via BokaDirekt och välj skin fade som tjänst." },
  { q: "Finns det parkering vid Tailor's Barber Åkersberga?", a: "Ja, gratis parkering direkt vid ICA Maxi Åkersberga." },
  { q: "Hur tar jag mig till Åkersberga med kollektivtrafik?", a: "Åkersberga nås med pendeltåg (Roslagsbanan) till Åkersberga station. Därifrån är det kort promenad eller buss till ICA Maxi." },
  { q: "Vad har Tailor's Barber Åkersberga för betyg?", a: `På Google har vi ${formatRating(stats.google.rating)} i betyg på ${formatCount(stats.google.count)} omdömen. På BokaDirekt ${formatRating(stats.bokadirekt.rating)} på ${formatCount(stats.bokadirekt.count)} omdömen. Vi visar alltid källorna var för sig.` },
  { q: "Gör ni skin fade i Åkersberga?", a: `Ja. Skin fade och taper fade är det vi gör mest i Åkersberga. Priset är ${PRICE.fade} kr och det tar 30 till 40 minuter. Säg low, mid eller high fade när du bokar, eller visa en bild.` },
  { q: "Kan man trimma skägget hos Tailor's Barber i Åkersberga?", a: `Ja. Skäggtrimning kostar ${PRICE.skaggtrimning} kr och görs med maskin och rakkniv, inklusive linjering av kanter och varm handduk. Klippning och skägg tillsammans kostar ${PRICE.klippningSkagg} kr.` },
  { q: "Erbjuder ni rakning med rakkniv i Åkersberga?", a: "Ja. Klassisk rakning med varm handduk och rakkniv bokas som egen tjänst eller som tillägg till klippning." },
];

const localBiz = {
  ...localBusinessSchema(salon, reviewsForSalon(salon.slug)),
  description: `Barbershop och frisör i ICA Maxi Åkersberga. Skin fade, taper fade, skäggtrimning och rakning med rakkniv. Herrklippning ${PRICE.herrklippning} kr. ${COUNT} omdömen, betyg ${RATING}.`,
  containedInPlace: {
    "@type": "ShoppingCenter",
    name: "ICA Maxi Huset Åkersberga",
    address: "Långhundravägen 9, Åkersberga",
  },
  hasMap: MAPS_LINK,
  priceRange: "$$",
  telephone: "+468371156",
  parentOrganization: { "@type": "Organization", name: "Tailor's Barber", url: SITE_URL },
};

export const Route = createFileRoute("/barberare-stockholm-akersberga")({
  head: () => ({
    meta: [
      { title: "Frisör Åkersberga: herrklippning 450 kr | Tailor's Barber" },
      { name: "description", content: `Frisör Åkersberga i ICA Maxi. Herrklippning 450 kr, skin fade, taper fade och skägg. Drop-in välkommet. Boka online.` },
      { property: "og:title", content: "Frisör Åkersberga: herrklippning 450 kr | Tailor's Barber" },
      { property: "og:description", content: "Frisör Åkersberga i ICA Maxi. Herrklippning 450 kr, skin fade, taper fade och skägg. Boka online." },
      { property: "og:url", content: absUrl(`${SITE_URL}/barberare-stockholm-akersberga/`) },
      { property: "og:image", content: absUrl(salon.image) },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: absUrl(`${SITE_URL}/barberare-stockholm-akersberga/`) },
      { rel: "preload", as: "image", href: akersbergaEntre1200, fetchpriority: "high" },
    ],

    scripts: [
      jsonLd(localBiz),
      jsonLd(faqSchema(faq)),
      jsonLd(breadcrumbSchema([
        { name: "Hem", url: "/" },
        { name: "Åkersberga", url: "/barberare-stockholm-akersberga/" },
      ])),
    ],
  }),
  component: AkersbergaPage,
});

function AkersbergaPage() {
  return (
    <SiteShell>
      <section className="container-page pt-6 md:pt-16 pb-4">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Åkersberga</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="eyebrow">Frisör i Åkersberga</div>
            <h1 className="mt-3 text-3xl md:text-5xl lg:text-6xl leading-[1.05]">
              Frisör Åkersberga: Tailor's Barber i ICA Maxi
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground/80 max-w-prose leading-relaxed">
               Frisör Åkersberga hos Tailor's Barber finns inne i ICA Maxi med gratis parkering och busshållplats direkt utanför. Här får du skin fade, taper fade, skäggtrimning och klassisk rakning med rakkniv av barberare som gör det varje dag. Herrklippning {PRICE.herrklippning} kr, skäggtrimning {PRICE.skaggtrimning} kr, hår och skägg {PRICE.klippningSkagg} kr. Öppet alla dagar, drop-in i mån av tid.
            </p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground/70">
              <SalonRatingLines salon="akersberga" />
              <span>📍 Långhundravägen 9, Åkersberga</span>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 items-center">
              <Button asChild size="lg">
                <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka tid online</a>
              </Button>
              <span className="text-sm text-foreground/70">Drop-in välkommen i mån av tid</span>
            </div>
          </div>
          <div className="grid grid-cols-3 grid-rows-2 gap-3 aspect-[4/3]">
            <div className="col-span-2 row-span-2 rounded-2xl overflow-hidden bg-muted shadow-soft">
              <img src={akersbergaEntre1200} alt="Frisör Åkersberga, Tailor's Barber i ICA Maxi" width={1200} height={1601} fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
            </div>
            <div className="rounded-xl overflow-hidden bg-muted shadow-soft">
              <img src={akersbergaSalong800} alt="Barberarstation med fotovägg hos Tailor's Barber, frisör Åkersberga" width={800} height={1067} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>
            <div className="rounded-xl overflow-hidden bg-muted shadow-soft">
              <img src={akersbergaSalong2800} alt="Hårtvättstation hos Tailor's Barber, frisör Åkersberga" width={800} height={1067} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>

          </div>
        </div>
      </section>




      <section className="border-y border-border bg-secondary/40">
        <div className="container-page py-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="font-medium">Öppettider Åkersberga:</span>
          <span><strong>Mån–Fre</strong> 10:00–19:00</span>
          <span><strong>Lör</strong> 10:00–18:00</span>
          <span><strong>Sön</strong> 10:00–18:00</span>
        </div>
      </section>

       <section className="container-page py-12">
         <h2 className="text-2xl md:text-3xl">Fade, skägg och rakning i Åkersberga</h2>
         <div className="mt-5 grid gap-3 md:grid-cols-3">
           <article className="card-elev p-5">
             <h3 className="text-lg font-medium">Skin fade och taper fade</h3>
             <p className="mt-3 text-foreground/80">
               Vi bygger övergången i flera steg med maskin och rakkniv tills skarven inte syns. Low, mid eller high fade efter huvudform och hårväxt. {PRICE.fade} kr, 30 till 40 minuter. Läs mer om <Link to="/skin-fade-akersberga/" className="underline">skin fade i Åkersberga</Link>.
             </p>
           </article>
           <article className="card-elev p-5">
             <h3 className="text-lg font-medium"><Link to="/skaggtrimning/" className="underline">Skäggtrimning</Link> och formning</h3>
             <p className="mt-3 text-foreground/80">
               Skägget formas efter käklinje och ansiktsform, kanterna linjeras med rakkniv och avslutas med varm handduk och skäggolja. {PRICE.skaggtrimning} kr, eller {PRICE.klippningSkagg} kr tillsammans med klippning.
             </p>
           </article>
           <article className="card-elev p-5">
             <h3 className="text-lg font-medium">Klassisk rakning med rakkniv</h3>
             <p className="mt-3 text-foreground/80">
               Varm handduk, rakkräm och rakkniv för en slät rakning eller rena konturer. Boka som tillägg till klippning eller skäggtrimning.
             </p>
           </article>
         </div>
       </section>

      <section className="container-page py-12">
        <h2 className="text-2xl md:text-3xl">Frisör Åkersberga med barberarkompetens</h2>
        <p className="mt-4 text-foreground/80 max-w-3xl">
           Frisör Åkersberga med barberare som fokuserar på herrklippning, fade, skägg och rakning. Kunderna lyfter fram konsekvensen: du vet vad du får varje gång, oavsett vilken barberare du bokar. Boka tid online eller kom förbi på drop-in.
        </p>
        <ul className="mt-5 grid gap-3 md:grid-cols-2 max-w-3xl">
          <li className="card-elev p-5"><SalonRatingLines salon="akersberga" /></li>
          <li className="card-elev p-5"><strong>ICA Maxi Åkersberga</strong> – gratis parkering, enkelt att kombinera med ärenden</li>
          <li className="card-elev p-5"><strong>Öppet alla dagar</strong> – vardagar 10–19, helger 10–18</li>
          <li className="card-elev p-5"><strong>Nära Roslagsbanan</strong> – pendeltåg till Åkersberga station</li>
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl mb-5">Tjänster och priser – Åkersberga</h2>
        <div className="card-elev overflow-hidden max-w-3xl">
          <table className="w-full text-left">
            <tbody>
              {services.map(([name, price], i) => (
                <tr key={i} className={i > 0 ? "border-t border-border/60" : ""}>
                  <td className="px-5 py-4">{name}</td>
                  <td className="px-5 py-4 text-right font-medium whitespace-nowrap">{price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-foreground/70 max-w-3xl">
          Se hela vår <Link to="/priser/" className="underline">prislista</Link>.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Tjänster vi erbjuder i Åkersberga</h2>
        <ul className="mt-4 space-y-2 text-foreground/85 max-w-3xl list-disc list-inside">
          <li><Link to="/herrklippning/" className="underline">Herrklippning</Link> – 450 kr, ca 30–40 min</li>
          <li><Link to="/taper-fade/" className="underline">Taper Fade</Link> – gradvis övergång mot sidorna</li>
          <li><Link to="/skin-fade-akersberga/" className="underline">Skin Fade Åkersberga</Link> – razor-sharp precision</li>
          <li><Link to="/skaggtrimning/" className="underline">Skäggtrimning</Link> – 380 kr</li>
          <li><Link to="/herrklippning-skaggtrimning/" className="underline">Hår & Skägg-paket</Link> – komplett behandling, 680 kr</li>
          <li><Link to="/barnklippning-akersberga/" className="underline">Barnklippning Åkersberga</Link> – för barn upp till 10 år, 380 kr</li>
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl mb-6">Teamet på Åkersberga</h2>
        <p className="mt-0 mb-6 text-foreground/80">{salonBarberLine("akersberga")}</p>
        <BarberGrid barbers={SALON_BARBERARE["akersberga"].barbers} salonName="Åkersberga" />
      </section>

      <ReviewsBlock salon="akersberga" heading="Vad våra kunder säger om Åkersberga" />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Hitta till oss – Barberare i Åkersberga</h2>
        <div className="grid md:grid-cols-2 gap-8 mt-6">
          <div>
            <p className="text-foreground/85">
              <strong>Adress:</strong>{" "}
              <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer" className="underline">
                Långhundravägen 9, 184 44 Åkersberga (ICA Maxi Huset)
              </a>
            </p>
            <h3 className="mt-5 text-lg font-medium">Kollektivtrafik & parkering</h3>
            <ul className="mt-2 space-y-1 text-foreground/80">
              <li><strong>Pendeltåg:</strong> Roslagsbanan till Åkersberga station, sedan kort buss- eller promenadväg</li>
              <li><strong>Bil:</strong> Gratis parkering direkt vid ICA Maxi Åkersberga</li>
            </ul>
            <h3 className="mt-5 text-lg font-medium">Öppettider</h3>
            <ul className="mt-2 space-y-1 text-foreground/80">
              <li><strong>Mån–Fre:</strong> 10:00–19:00</li>
              <li><strong>Lör:</strong> 10:00–18:00</li>
              <li><strong>Sön:</strong> 10:00–18:00</li>
            </ul>
          </div>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-muted shadow-soft min-h-[280px]">
            <MapEmbed src={MAPS_EMBED} title="Karta över Tailor's Barber Åkersberga" href={MAPS_LINK} />
          </div>
        </div>
      </section>

      <section className="container-page py-10">
        <div className="card-elev p-8 md:p-12 bg-ink text-cream">
          <div className="eyebrow text-brass">Boka eller drop-in</div>
          <h2 className="mt-3 text-3xl md:text-4xl text-cream">Boka barberare i Åkersberga</h2>
          <p className="mt-4 max-w-prose text-cream/80">
            Boka din tid online – välj barberare och klockslag som passar dig. Drop-in välkommen i mån av plats. Så fungerar <Link to="/drop-in-barber-stockholm/" className="underline">drop-in hos barberare i Stockholm</Link>.
          </p>
          <div className="mt-6">
            <Button asChild size="lg" className="bg-brass text-ink hover:bg-brass/90">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka tid online – Åkersberga</a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Vad skiljer en barberare från en frisör?</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Många i Åkersberga söker efter frisör när de egentligen vill ha en herrklippning eller ett skägg
          fixat. Skillnaden är inriktningen: en frisör täcker både dam- och herrhår, färg och längre
          frisyrer, medan en barberare arbetar med herrhår och skägg dagligen – maskin, sax och rakkniv,
          med tyngdpunkt på korta klippningar och övergångar.
        </p>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Det märks framför allt på detaljerna: hur mjukt faden går över, hur ren nacklinjen är och hur
          skägget följer käklinjen. Vill du ha kort eller medellångt hår, en fade eller ett välformat
          skägg är barbershopen rätt ställe. Handlar det om färgning eller en längre frisyr är en
          frisörsalong bättre lämpad.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Fler Tailor's Barber-salonger</h2>
        <ul className="mt-4 space-y-2 text-foreground/85 max-w-3xl list-disc list-inside">
          <li><Link to="/barberare-stockholm-taby/" className="underline">Täby Centrum</Link> – Westfield Täby Centrum, flaggskeppssalong</li>
          <li><Link to="/barberare-stockholm-arninge/" className="underline">Arninge</Link> – ICA Maxi Arninge</li>
          <li><Link to="/barberare-stockholm-ahlens-city/" className="underline">Åhléns City Stockholm</Link> – 1 minut från T-Centralen</li>
        </ul>
      </section>

      <ContextLinkBlock
        heading="Tjänster i Åkersberga"
        links={SALON_SERVICE_LINKS["akersberga"]!.links}
      />

      <Faq items={faq} heading="Vanliga frågor – Barberare Åkersberga" />

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Redo att boka i Åkersberga?</h2>
          <div className="mt-3 text-foreground/80">
            <p>ICA Maxi Åkersberga.</p>
            <SalonRatingLines salon="akersberga" className="mt-2" />
          </div>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">Boka online</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
